/**
 * RetryableSupabaseClient - Wrapper for Supabase client with automatic retry logic
 * Provides retry capabilities for all Supabase operations with circuit breaker protection
 */

import { retryWithBackoff, getCircuitBreakerState } from '../utils/retryMechanism';
import { errorLogger } from '../utils/errorLogger';

/**
 * RetryableSupabaseClient wrapper class
 */
export class RetryableSupabaseClient {
  constructor(supabaseClient, retryConfig = {}) {
    this.client = supabaseClient;
    this.retryConfig = retryConfig;
    this.retryCallbacks = [];
  }

  /**
   * Register callback for retry events
   */
  onRetry(callback) {
    this.retryCallbacks?.push(callback);
  }

  /**
   * Notify all retry callbacks
   */
  notifyRetry(attempt, maxRetries, delay, error) {
    this.retryCallbacks?.forEach((callback) => {
      try {
        callback(attempt, maxRetries, delay, error);
      } catch (err) {
        console.error('[RetryableSupabaseClient] Error in retry callback:', err);
      }
    });
  }

  /**
   * Execute query with retry logic
   */
  async retryQuery(queryBuilder, context = {}) {
    return retryWithBackoff(
      async () => {
        const result = await queryBuilder;
        
        // Check for Supabase error
        if (result?.error) {
          const error = new Error(result.error.message);
          error.code = result?.error?.code;
          error.details = result?.error?.details;
          error.hint = result?.error?.hint;
          error.status = result?.error?.status;
          throw error;
        }
        
        return result;
      },
      this.retryConfig,
      (attempt, maxRetries, delay, error) => {
        this.notifyRetry(attempt, maxRetries, delay, error);
        
        // Log query retry
        errorLogger?.logQueryError(error, {
          ...context,
          retryAttempt: attempt,
          maxRetries,
          nextRetryDelay: delay,
        });
      }
    );
  }

  /**
   * Execute insert operation with retry
   */
  async retryInsert(table, data, options = {}) {
    const context = {
      table,
      operation: 'INSERT',
      query: `Insert into ${table}`,
    };

    return this.retryQuery(
      this.client?.from(table)?.insert(data, options),
      context
    );
  }

  /**
   * Execute update operation with retry
   */
  async retryUpdate(table, data, matchConditions = {}) {
    const context = {
      table,
      operation: 'UPDATE',
      query: `Update ${table}`,
    };

    let query = this.client?.from(table)?.update(data);
    
    // Apply match conditions
    Object.entries(matchConditions)?.forEach(([key, value]) => {
      query = query?.eq(key, value);
    });

    return this.retryQuery(query, context);
  }

  /**
   * Execute delete operation with retry
   */
  async retryDelete(table, matchConditions = {}) {
    const context = {
      table,
      operation: 'DELETE',
      query: `Delete from ${table}`,
    };

    let query = this.client?.from(table)?.delete();
    
    // Apply match conditions
    Object.entries(matchConditions)?.forEach(([key, value]) => {
      query = query?.eq(key, value);
    });

    return this.retryQuery(query, context);
  }

  /**
   * Execute select operation with retry
   */
  async retrySelect(table, columns = '*', filters = {}) {
    const context = {
      table,
      operation: 'SELECT',
      query: `Select from ${table}`,
    };

    let query = this.client?.from(table)?.select(columns);
    
    // Apply filters
    Object.entries(filters)?.forEach(([key, value]) => {
      if (typeof value === 'object' && value !== null) {
        // Handle complex filters (gt, lt, etc.)
        Object.entries(value)?.forEach(([operator, operatorValue]) => {
          if (query?.[operator]) {
            query = query?.[operator](key, operatorValue);
          }
        });
      } else {
        query = query?.eq(key, value);
      }
    });

    return this.retryQuery(query, context);
  }

  /**
   * Execute auth operation with retry
   */
  async retryAuth(authOperation, operationName = 'auth') {
    return retryWithBackoff(
      async () => {
        const result = await authOperation();
        
        // Check for auth error
        if (result?.error) {
          const error = new Error(result.error.message);
          error.code = result?.error?.code;
          error.status = result?.error?.status;
          throw error;
        }
        
        return result;
      },
      this.retryConfig,
      (attempt, maxRetries, delay, error) => {
        this.notifyRetry(attempt, maxRetries, delay, error);
        
        // Log auth retry
        errorLogger?.logAuthError(error, {
          action: operationName,
          retryAttempt: attempt,
          maxRetries,
          nextRetryDelay: delay,
        });
      }
    );
  }

  /**
   * Execute storage operation with retry
   */
  async retryStorage(storageOperation, operationName = 'storage') {
    return retryWithBackoff(
      async () => {
        const result = await storageOperation();
        
        // Check for storage error
        if (result?.error) {
          const error = new Error(result.error.message);
          error.code = result?.error?.code;
          error.status = result?.error?.status;
          throw error;
        }
        
        return result;
      },
      this.retryConfig,
      (attempt, maxRetries, delay, error) => {
        this.notifyRetry(attempt, maxRetries, delay, error);
        
        // Log storage retry
        errorLogger?.logError(error, {
          component: 'Storage',
          operation: operationName,
          retryAttempt: attempt,
          maxRetries,
          nextRetryDelay: delay,
        });
      }
    );
  }

  /**
   * Get underlying Supabase client (for operations that don't need retry)
   */
  getClient() {
    return this.client;
  }

  /**
   * Check if circuit breaker is open
   */
  isCircuitOpen() {
    const state = getCircuitBreakerState();
    return state?.state === 'open';
  }

  /**
   * Get circuit breaker status
   */
  getCircuitStatus() {
    return getCircuitBreakerState();
  }
}

/**
 * Create retryable Supabase client
 */
export const createRetryableClient = (supabaseClient, retryConfig = {}) => {
  return new RetryableSupabaseClient(supabaseClient, retryConfig);
};
